import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { urlDeImagenDePerfil } from '../utils/imagen-perfil.util';

/** Solución del workspace, ya normalizada para pintarla y para navegar a ella. */
export interface SolucionAccesible {
  id: string;
  nombre: string;
  /** Imagen que envía el backend; null mientras no se haya subido ninguna. */
  imagenUrl: string | null;
  /** Dirección de la solución; null si todavía no tiene una. */
  url: string | null;
  /** La solución en la que ya se está trabajando, o sea esta aplicación. */
  actual: boolean;
}

/** Solución del catálogo, tal como la devuelve Console. */
interface SolucionRemota {
  id?: string;
  solutionCode?: string;
  name?: string;
  // Imagen y dirección propias de la solución. Hoy llegan en null: se cargan
  // por otro endpoint. En cuanto tengan valor mandan sobre todo lo demás.
  imagePath?: string | null;
  pathUrl?: string | null;
}

/** Fila del listado: la solución habilitada para este workspace. */
interface WorkspaceSolutionRemota {
  workspaceSolutionId?: string;
  solutionId?: string;
  disabledAt?: string | null;
  solution?: SolucionRemota | null;
}

/**
 * Direcciones conocidas mientras el backend no envía `pathUrl`. Es un puente:
 * la que llegue del backend siempre manda sobre esta tabla, así que cuando se
 * carguen las direcciones reales esto deja de usarse solo.
 *
 * Las claves son el `solutionCode` normalizado.
 */
const URL_POR_SOLUCION: Record<string, string> = {
  // Cambia por entorno, así que viene del environment.
  transport: environment.transportAppUrl,
  // Esta misma aplicación, que así queda marcada como la solución abierta.
  maintenance: window.location.origin,
};

/**
 * El Dashboard no es una solución del catálogo, así que no viene en el listado:
 * se agrega a mano, siempre al final, con su propio ícono fijo (`DASHBOARD_ICON`).
 *
 * La dirección apunta a `/dashboard` y no a la raíz: Console resuelve ahí la
 * sesión que llega por enlace, igual que Transport Operations. Entrar por la
 * raíz dejaba al usuario en el formulario de inicio de sesión. El dominio de
 * Console cambia por entorno (`consoleAppUrl`).
 */
const DASHBOARD = {
  id: 'dashboard',
  nombre: 'Dashboard',
  url: `${environment.consoleAppUrl}/dashboard`,
};

/**
 * Ícono fijo del Dashboard: el mismo asset del favicon (círculo negro con el
 * isotipo en blanco), no la imagen propia del workspace. Al traer su propio
 * fondo funciona igual sobre el fondo blanco fijo de `.solution-logo`.
 */
const DASHBOARD_ICON = 'Assets/icons/Myservy Icon white.png';

@Injectable({ providedIn: 'root' })
export class SolucionesService {
  private http = inject(HttpClient);
  private cache = new Map<string, SolucionAccesible[]>();

  /**
   * Soluciones habilitadas para el workspace, en el orden que envía el backend.
   *
   * Se cachea por workspace porque el listado se abre desde el menú y cambia
   * poco: no tiene sentido ir al backend cada vez que se despliega.
   */
  async obtener(workspaceId: string): Promise<SolucionAccesible[]> {
    const id = workspaceId?.trim();
    if (!id) return [];

    const enCache = this.cache.get(id);
    if (enCache) return enCache;

    let remotas: WorkspaceSolutionRemota[] = [];
    try {
      remotas = await firstValueFrom(
        this.http.get<WorkspaceSolutionRemota[]>(
          `${environment.consoleApiUrl}/v1/workspaces/${id}/solutions`,
          { withCredentials: true },
        ),
      );
    } catch {
      // Sin respuesta no hay nada que ofrecer. No se cachea el vacío para que
      // el siguiente intento vuelva a preguntar.
      return [];
    }

    const habilitadas = (remotas ?? [])
      // Una solución que le dieron de baja al workspace no se ofrece.
      .filter(remota => !remota?.disabledAt);

    const soluciones = habilitadas
      .map((remota, indice) => this.normalizar(remota, indice))
      .filter((s): s is SolucionAccesible => s !== null);

    const dashboard = this.ofreceDashboard(id) ? this.dashboard(habilitadas) : null;
    if (dashboard) soluciones.push(dashboard);

    this.cache.set(id, soluciones);
    return soluciones;
  }

  /** Olvida lo cacheado. Al cambiar de workspace cambian las soluciones. */
  limpiar(): void {
    this.cache.clear();
  }

  /**
   * Agrega el workspace activo a la dirección de destino. Es lo que permite
   * entrar a la otra solución sin volver a iniciar sesión: la sesión viaja en
   * la cookie del dominio y el workspace, en la URL.
   */
  conWorkspaceId(url: string, workspaceId: string): string {
    if (!workspaceId) return url;

    try {
      const destino = new URL(url);
      destino.searchParams.set('workspaceId', workspaceId);
      return destino.toString();
    } catch {
      const separador = url.includes('?') ? '&' : '?';
      return `${url}${separador}workspaceId=${encodeURIComponent(workspaceId)}`;
    }
  }

  private normalizar(remota: WorkspaceSolutionRemota, indice: number): SolucionAccesible | null {
    const solucion = remota?.solution ?? null;
    const nombre = solucion?.name?.trim() ?? '';
    // Una solución sin nombre no se puede ni mostrar ni elegir.
    if (!nombre) return null;

    const url = this.resolverUrl(solucion, nombre);

    return {
      id: remota.workspaceSolutionId || remota.solutionId || `${nombre}-${indice}`,
      nombre,
      imagenUrl: this.resolverImagen(solucion),
      url,
      actual: this.esLaActual(url),
    };
  }

  private resolverUrl(solucion: SolucionRemota | null, nombre: string): string | null {
    const delBackend = (solucion?.pathUrl ?? '').trim();
    if (delBackend) return delBackend;

    const clave = this.clave(solucion?.solutionCode ?? '') || this.clave(nombre);
    return URL_POR_SOLUCION[clave] ?? null;
  }

  /**
   * La imagen se guarda como ruta relativa ("imagenes/algo.png") y vive en el
   * host de identidad, el mismo que sirve las fotos de perfil. Sale de la
   * solución y sólo de ahí: la que viene en la fila es la del workspace, y
   * usarla pintaría el mismo logo en todas las tarjetas.
   */
  private resolverImagen(solucion: SolucionRemota | null): string | null {
    return urlDeImagenDePerfil(solucion?.imagePath ?? '') || null;
  }

  /**
   * El Dashboard, con su ícono fijo. Si algún día el backend lo devuelve como
   * una solución más, la del listado manda y esta no se agrega, para no
   * ofrecer el mismo destino dos veces.
   */
  /**
   * Si a este workspace se le ofrece el Dashboard central.
   *
   * La lista sale del entorno --`workspacesSinDashboardCentral`-- y se quita
   * un id de ahi para volver a ofrecerlo, sin tocar esta clase. Vacia por
   * defecto: sin configuracion se comporta como siempre.
   *
   * Esto saca la entrada del listado, no cierra la puerta: quien conozca la
   * direccion de Console entra igual. El control de verdad vive alla.
   */
  private ofreceDashboard(workspaceId: string): boolean {
    const excluidos = environment.workspacesSinDashboardCentral ?? [];
    return !excluidos.includes(workspaceId);
  }

  private dashboard(remotas: WorkspaceSolutionRemota[]): SolucionAccesible | null {
    const yaEsta = remotas.some(
      remota => this.mismoOrigen(remota?.solution?.pathUrl ?? null, DASHBOARD.url),
    );
    if (yaEsta) return null;

    return {
      ...DASHBOARD,
      imagenUrl: DASHBOARD_ICON,
      actual: this.esLaActual(DASHBOARD.url),
    };
  }

  /** La solución abierta ahora mismo: la que apunta a este mismo origen. */
  private esLaActual(url: string | null): boolean {
    return this.mismoOrigen(url, window.location.origin);
  }

  private mismoOrigen(url: string | null, referencia: string): boolean {
    if (!url) return false;
    try {
      return new URL(url).origin === new URL(referencia).origin;
    } catch {
      return false;
    }
  }

  /** "Transport Operations" y "transport-operations" resuelven a la misma clave. */
  private clave(valor: string): string {
    return valor
      .normalize('NFD')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }
}
